import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { TableStore } from 'shared/ui/TableNew'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { RequestLoadData } from 'shared/api/lib'
import { TaggedContactsRow } from 'entities/Tags/model/TaggedContactsRow'
import { type IParamsContactsTagsById, TagsApi } from 'entities/Tags'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseContacts } from 'entities/Contacts/api/types'
import { type Contact } from 'entities/Contacts/model/Contact'
import { openConversationByContact } from 'features/OpenConversation'
import {
  contactsDetailsGlobalStore,
  ContactsDetailsManageStore,
} from 'widgets/ContactsDetails/store'
import { TagsModalStore } from 'pages/settings/pages/tags/store/TagsModalStore'
import { SettingsRoutes } from 'pages/settings/route/type'

export class TaggedContactsStore {
  private readonly _tableStore: TableStore<TaggedContactsRow>
  private readonly _contactsDetailsManageStore: ContactsDetailsManageStore
  private _tagsModalStore: TagsModalStore | null = null
  private _initialLoading = true
  private _loading = false
  private _listMap = new Map<number, TaggedContactsRow>()
  private _search = ''
  private _page = 1
  private _total = 0
  private _limit = 10
  private _tagName = ''
  private _disposeReaction: IReactionDisposer | null = null
  private _visibleUI = false
  private _idsToDelete: number[] = []
  private _disposed = false

  constructor(private readonly _id: number, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._tableStore = new TableStore({
      key: 'tagged_contacts',
      element: 'number',
      page: this._page,
      limit: this._limit,
    })

    this._disposeReaction = reaction(
      () => this._listRequestParams,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  init = async () => {
    try {
      const [_, __, { data }] = await Promise.all([
        this._entities.userPermissionsStore.initPromise,
        this._entities.usersStore.initPromise,
        TagsApi.getTagById(this._id),
      ])

      await this.loadData()

      runInAction(() => {
        this._tagName = data ? data.label : ''
        this._tagsModalStore = new TagsModalStore(
          this.isUserCanManageTags,
          this.onSuccessModal,
          this.resetData
        )
      })

      if (!this._disposed) {
        this._contactsDetailsManageStore.init()
      }
    } catch (e) {
      logger.error(e)

      if (this._visibleUI) this.checkBackToTags(e)
    } finally {
      runInAction(() => {
        this._initialLoading = false
      })
    }

    return this.dispose
  }

  onSuccessModal = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await TagsApi.getTagById(this._id)

      runInAction(() => {
        this._tagName = data ? data.label : ''
      })

      await this.loadData()
    } catch (e) {
      logger.error(e)

      if (this._visibleUI) this.checkBackToTags(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._disposed = true
    this._disposeReaction?.()
    this._requestLoadData.dispose()
    this._tagsModalStore?.dispose()
    this._contactsDetailsManageStore.reset()
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._tableStore.setLoading(true)
        this._loading = true
      })

      const { data } = await TagsApi.getContactsTagsById(this._id, this._listRequestParams, {
        cancelToken: cancelRequest.token,
      })

      this.setData(data)
    },
    errorCallback: () => {
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again.',
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
        this._loading = false
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  setData = ({ data, total, current_page }: IResponseContacts) => {
    this._listMap.clear()
    const rows = data.map((item) => new TaggedContactsRow(item))

    rows.forEach((row) => {
      this._listMap.set(row.id, row)
    })

    this._tableStore.setRows(rows)
    this._tableStore.setTotal(total)
    this._tableStore.setPage(current_page)
    this._page = current_page
    this._total = total
  }

  resetData = () => {
    this._search = ''
    this._page = this.calculatePageAfterChanges()
    this._tableStore.resetSelected()
    this._total = 0
    this.setIdsToDelete([])
  }

  private calculatePageAfterChanges() {
    const deletedCount = this._idsToDelete.length
    const newTotal = this._total - deletedCount
    const newLastPage = newTotal > 0 ? Math.ceil(newTotal / this._limit) : 1

    if (this._page > newLastPage) {
      return newLastPage
    }

    return this._page
  }

  checkBackToTags(e: unknown) {
    if (e instanceof AxiosError && e.response?.status === 404) {
      uiStore.changeRouteViaVue({
        path: `/${SettingsRoutes.root}/${SettingsRoutes.tags}`,
      })
    }
  }

  onBulkDelete = () => {
    this.setIdsToDelete(this._tableStore.selectedIds)
    this.tagsModalStore?.onDeleteTagFromContact(this._id, this._tableStore.selectedIds)
  }

  onTagRename = () => {
    this.tagsModalStore?.onRename(this._id, this._tagName)
  }

  onTagDelete = () => {
    this.tagsModalStore?.onDelete(this._id, this._tagName, true)
  }

  onContactTagRemove = (contactId: number) => {
    this.setIdsToDelete([contactId])
    this.tagsModalStore?.onDeleteTagFromContact(this._id, [contactId])
  }

  onShowDetails = async (id: number) => {
    try {
      const contact = await this._entities.contactsStore.getById(id, true)

      if (contact && this._visibleUI) {
        this._contactsDetailsManageStore.setContactsDetails({
          contactId: contact.id,
          variant: 'contacts',
          profileCardProps: {
            onClickMessage: (contact: Contact) => {
              runInAction(() => {
                this._initialLoading = true
              })

              openConversationByContact(this._entities)(contact).finally(() =>
                runInAction(() => {
                  this._initialLoading = false
                })
              )
            },
            enableChat: true,
          },
          onCloseContactDetails: () => {
            contactsDetailsGlobalStore.handleChangeCollapse(true)
            this._contactsDetailsManageStore.resetContactsDetails()
          },
          notesProps: {
            hideMessageFieldActions: ['ai_assistant'],
          },
        })

        contactsDetailsGlobalStore.handleChangeCollapse(false)
        contactsDetailsGlobalStore.setOverlay(true)
        contactsDetailsGlobalStore.openBlockView('tags')
      }
    } catch (e) {
      logger.error(e)

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again.',
      })
    }
  }

  changePaging = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
    this._loading = true
  }

  setSearch = (value: string) => {
    this._page = 1
    this._search = value
    this._loading = true
  }

  setIdsToDelete = (ids: number[]) => {
    this._idsToDelete = ids
  }

  setVisibleUI = (status: boolean) => {
    this._visibleUI = status
  }

  private get _listRequestParams() {
    const params: IParamsContactsTagsById = {
      page: this._page,
      limit: this._limit,
      sortBy: this._tableStore.sortBy || 'created_at',
      sortOrder: this._tableStore.sortOrder,
    }

    if (this._search) params.search = this._search

    return params
  }

  get initialLoading() {
    return this._initialLoading
  }

  get loading() {
    return this._loading
  }

  get search() {
    return this._search
  }

  get tableStore() {
    return this._tableStore
  }

  get tagsModalStore() {
    return this._tagsModalStore
  }

  get items() {
    return Array.from(this._listMap.values())
  }

  get isEmpty() {
    return !this._initialLoading && !this._loading && !this._listMap.size && !this._search.length
  }

  get isUserCanManageTags() {
    return (
      this._entities.userPermissionsStore.getItem('can_manage_tags') &&
      !this._entities.usersStore.user?.isViewOnlyRole
    )
  }

  get tagName() {
    return this._tagName
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }
}
